import type { ComponentChildren } from "preact";
import { useId, useRef, useState } from "preact/hooks";
import { parseDecimal } from "../../settings/sensitivity";
import { cx } from "../cx";

export interface SliderProps {
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  label: ComponentChildren;
  description?: ComponentChildren;
  /** Suffix shown inside the numeric field, e.g. "°" or "x". */
  unit?: string;
  /** Digits shown after the decimal point; defaults to the precision of `step`. */
  decimals?: number;
  disabled?: boolean;
  class?: string;
}

function stepDecimals(step: number): number {
  const frac = String(step).split(".")[1];
  return frac ? frac.length : 0;
}

/** Range slider with a numeric field. Typing commits on Enter or blur and is clamped to the range. */
export function Slider({
  value,
  min,
  max,
  step,
  onChange,
  label,
  description,
  unit,
  decimals,
  disabled = false,
  class: cls,
}: SliderProps) {
  const id = useId();
  const digits = decimals ?? stepDecimals(step);
  const [draft, setDraft] = useState<string | null>(null);
  // Mirrors `draft` synchronously: Enter and Escape blur the field before the state update renders,
  // and that blur handler must already see the edit as finished (Escape must not commit it).
  const pending = useRef<string | null>(null);
  const clamped = Math.min(max, Math.max(min, value));
  const pct = max > min ? ((clamped - min) / (max - min)) * 100 : 0;

  const setText = (next: string | null) => {
    pending.current = next;
    setDraft(next);
  };

  const commit = () => {
    const text = pending.current;
    if (text === null) return;
    const n = parseDecimal(text);
    setText(null);
    if (n !== null) onChange(Math.min(max, Math.max(min, n)));
  };

  return (
    <div class={cx("slider", disabled && "is-disabled", cls)}>
      <label class="slider__text" for={id}>
        <span class="slider__label">{label}</span>
        {description && <span class="slider__desc">{description}</span>}
      </label>
      <input
        id={id}
        class="slider__range"
        type="range"
        min={min}
        max={max}
        step={step}
        value={clamped}
        disabled={disabled}
        style={{ "--pct": `${pct}%` }}
        onInput={(e) => onChange(Number(e.currentTarget.value))}
        data-nav=""
      />
      <span class="slider__field">
        <input
          class="slider__num"
          data-decimal={digits > 0 || undefined}
          type="text"
          inputMode="decimal"
          aria-label="Value"
          value={draft ?? clamped.toFixed(digits)}
          disabled={disabled}
          onFocus={(e) => e.currentTarget.select()}
          onInput={(e) => setText(e.currentTarget.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              commit();
              e.currentTarget.blur();
            } else if (e.key === "Escape" && pending.current !== null) {
              e.stopPropagation(); // cancel the edit, do not leave the screen
              setText(null);
              e.currentTarget.blur();
            }
          }}
          data-nav=""
        />
        {unit && <span class="slider__unit">{unit}</span>}
      </span>
    </div>
  );
}
